import * as React from "react";
import {FormBodyProps} from "../../common/props/FormBodyProps";
import SectionView from "../../layout/section/SectionView";
import Row from "../../layout/Row";
import Col from "../../layout/Col";
import QuoteForm from "./QuoteForm";
import Quote from "../../../model/quote/Quote";
import Amount from "../../../model/common/Amount";
import TextAreaFormGroup from "../../ui/form/TextAreaFormGroup";
import ButtonTertiary from "../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import PriceAdjustSlider from "./PriceAdjustSlider";
import {QuoteCalculator, QuoteCalculatorInputData} from "../../../utils/QuoteCalculator";
import {option} from "ts-option";

interface ComponentProps {
	/**
	 * Quote
	 */
	quote: Quote;
	/**
	 * On quote change
	 * @param quote
	 */
	onQuoteChange: (quote: Quote) => void
}

interface State {
	/**
	 * Indicates if total price slider should be visible
	 */
	showPriceSlider: boolean
}

type Props = FormBodyProps<QuoteForm> & ComponentProps;

export default class ReviewQuoteFormBody extends React.PureComponent<Props, State> {

	minTotalPrice: Amount;
	maxTotalPrice: Amount;
	calculatorInputData: QuoteCalculatorInputData;

	constructor(props: Props) {
		super(props);
		this.state = {
			showPriceSlider: false
		}
	}

	render() {
		const {quote} = this.props;
		return <SectionView customClass={"c-quote"}>
			<div className={"quote-review"}>
				<Row>
					<Col xl={12} md={12}>
						<h1>Review quotation</h1>
						<h4>Please review your quote item selections before proceeding</h4>
					</Col>
				</Row>
				<Row>
					<Col xl={12} md={12}>
						<div className={"quote-items-row qheader"}>
							<div className={"item-name"}>Item name</div>
							<div className={"item-price"}>Unit price</div>
							<div className={"item-quantity"}>Quantity</div>
							<div className={"item-price"}>Sub-total</div>
						</div>
						{quote.items
							.map(i => this.renderItem(i.assetId, i.name, i.unitPriceWithVat.getOrElse(i.unitPrice), i.quantity,
								i.getTotalPrice()))}
						{this.renderLabourItem("LabourInstallation", "Installation labour", quote.labourTotalCost)}
					</Col>
				</Row>
				<Row>
					<Col xl={12} md={12}>
						<div className={"quote-items-price-summary-row"}>
							<div className={"text"}>Markup</div>
							<div className={"price"}>{quote.markupAmount.format()}</div>
						</div>
						{quote.vatRegistered && <div className={"quote-items-price-summary-row"}>
							<div className={"text"}>Total before VAT</div>
							<div className={"price"}>{quote.totalPriceBeforeVat.format()}</div>
						</div>}
						{quote.vatRegistered && <div className={"quote-items-price-summary-row"}>
							<div className={"text"}>VAT ({quote.vat}%)</div>
							<div className={"price"}>{quote.vatAmount.map(va => va.format()).orNull}</div>
						</div>}
						<div className={"quote-items-price-summary-row"}>
							{!this.state.showPriceSlider &&
							<ButtonTertiary label={"Adjust price"}
											onClick={this.showPriceSlider}/>}
							<div style={{flex: 'none'}} className={"text bold"}>Total including VAT</div>
							<div className={"price bold"}>{quote.totalPrice.format()}</div>
						</div>
					</Col>
				</Row>
				{this.state.showPriceSlider && <Row>
					<Col xl={12} md={12}>
						<div style={{marginBottom: "24px"}}>
							<PriceAdjustSlider startValue={this.props.quote.totalPrice}
											   min={this.minTotalPrice}
											   max={this.maxTotalPrice}
											   onChanged={this.adjustPrices}/>
						</div>
					</Col>
				</Row>}
				<Row>
					<Col xl={12} md={12}>
						<TextAreaFormGroup label="Quote description"
										   rows={8}
										   field={this.props.form.description}
										   focus={this.props.form.hasFocus(this.props.form.description)}
										   onChange={field => this.props.onFieldChange(field)}/>
					</Col>
				</Row>
			</div>
		</SectionView>;
	}

	showPriceSlider = () => {
		this.calculatorInputData = {
			markup: this.props.form.markup.getValue(),
			vat: this.props.form.vat.getValue(),
			vatRegistered: this.props.form.vatRegistered.getValue(),
			items: this.props.form.items.getValue(),
			maxFee: this.props.form.maxFee.getValue()
		};
		this.minTotalPrice = QuoteCalculator.calculateFromLabourCost(new Amount(0), this.calculatorInputData).totalPrice;
		this.maxTotalPrice = QuoteCalculator.calculateFromLabourCost(new Amount(1000000), this.calculatorInputData).totalPrice;
		this.setState({showPriceSlider: true});
	};

	adjustPrices = (newTotalPrice: number) => {
		const calcData = QuoteCalculator.calculateFromTotal(new Amount(newTotalPrice * 100), this.calculatorInputData);
		const {
			labourTotalCost,
			totalPrice,
			totalPriceBeforeVat,
			totalVat,
			totalMargin,
			hiberCommission,
			installerGross
		} = calcData;
		this.props.onQuoteChange(this.props.quote.copy({
			labourTotalCost,
			totalPrice,
			totalPriceBeforeVat,
			vatAmount: totalVat,
			markupAmount: totalMargin,
			hiberCommission: option(hiberCommission),
			installerGross: option(installerGross)
		}));

	};

	renderItem(key: string, name: string, price: Amount, quantity: number, totalPrice: Amount) {
		return (
			<div key={key} className={"quote-items-row"}>
				<div className={"item-name"}>{name}</div>
				<div className={"item-price"}>{price.format()}</div>
				<div className={"item-quantity"}>{quantity.toFixed(2)}</div>
				<div className={"item-price"}>{totalPrice.format()}</div>
			</div>
		)
	}

	renderLabourItem(key: string, name: string, totalPrice: Amount) {
		return (
			<div key={key} className={"quote-items-row"}>
				<div className={"item-name"}>{name}</div>
				<div className={"item-price"}/>
				<div className={"item-quantity"}/>
				<div className={"item-price"}>{totalPrice.format()}</div>
			</div>
		)
	}
}
